import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { Lock, LogOut, RefreshCw } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const KINDS = ["all", "property", "investment", "it", "digital", "support"];
const KIND_STYLE = {
  property: "bg-stone text-charcoal",
  investment: "bg-brass text-ink",
  it: "bg-eeteal text-white",
  digital: "bg-[#0d9488] text-white",
  support: "bg-[#f4f1eb] text-[#0a0a0a]",
};

export default function Admin() {
  const [token, setToken] = useState(() => sessionStorage.getItem("ee-admin-token") || "");
  const [password, setPassword] = useState("");
  const [error, setError] = useState(false);
  const [subs, setSubs] = useState([]);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(false);

  const load = useCallback(async (tok) => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/admin/submissions`, {
        headers: { Authorization: `Bearer ${tok}` },
      });
      if (!res.ok) throw new Error("unauthorized");
      const data = await res.json();
      setSubs(data.submissions);
    } catch (e) {
      setToken("");
      sessionStorage.removeItem("ee-admin-token");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (token) load(token); }, [token, load]);

  const login = async (e) => {
    e.preventDefault();
    setError(false);
    try {
      const res = await fetch(`${API}/admin/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ password }),
      });
      if (!res.ok) throw new Error("wrong");
      const data = await res.json();
      sessionStorage.setItem("ee-admin-token", data.token);
      setToken(data.token);
    } catch (err) {
      setError(true);
    }
  };

  if (!token) {
    return (
      <main className="grain min-h-screen bg-[#0a0a0a] text-white flex items-center justify-center px-6" data-testid="admin-login">
        <form onSubmit={login} className="w-full max-w-sm border border-white/15 p-8" data-testid="admin-login-form">
          <Lock className="h-5 w-5 text-brass mb-5" />
          <h1 className="font-gateway font-bold tracking-[0.2em] text-lg mb-1">EAGLE EYES</h1>
          <p className="font-mono-data text-[0.6rem] tracking-[0.24em] text-white/40 mb-7">ADMIN INBOX</p>
          <input
            data-testid="admin-password-input"
            type="password"
            required
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full bg-transparent border border-white/25 focus:border-brass px-4 py-3 text-sm mb-3 transition-colors"
          />
          {error && <p data-testid="admin-login-error" className="text-[0.75rem] text-crimson mb-3">Wrong password.</p>}
          <button data-testid="admin-login-button" type="submit" className="w-full bg-brass text-ink font-mono-data text-[0.66rem] tracking-[0.2em] px-4 py-3.5 hover:bg-paper transition-colors">
            UNLOCK
          </button>
          <Link to="/" data-testid="admin-back-home" className="block text-center font-mono-data text-[0.58rem] tracking-[0.2em] text-white/35 hover:text-white/70 mt-5 transition-colors">← EAGLE EYES</Link>
        </form>
      </main>
    );
  }

  const shown = filter === "all" ? subs : subs.filter((s) => s.kind === filter);

  return (
    <main className="grain min-h-screen bg-[#0a0a0a] text-white px-6 md:px-10 py-8" data-testid="admin-inbox">
      <header className="flex flex-wrap items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="font-gateway font-bold tracking-[0.2em] text-lg">EAGLE EYES</h1>
          <p className="font-mono-data text-[0.6rem] tracking-[0.24em] text-white/40">ADMIN INBOX — {subs.length} SUBMISSIONS</p>
        </div>
        <div className="flex items-center gap-3">
          <button data-testid="admin-refresh" onClick={() => load(token)} className="flex items-center gap-2 border border-white/25 px-3.5 py-2 font-mono-data text-[0.6rem] tracking-[0.18em] hover:bg-white/10 transition-colors">
            <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} /> REFRESH
          </button>
          <button data-testid="admin-logout" onClick={() => { sessionStorage.removeItem("ee-admin-token"); setToken(""); }} className="flex items-center gap-2 border border-white/25 px-3.5 py-2 font-mono-data text-[0.6rem] tracking-[0.18em] hover:bg-white/10 transition-colors">
            <LogOut className="h-3.5 w-3.5" /> LOGOUT
          </button>
        </div>
      </header>

      <div className="flex flex-wrap gap-2 mb-6">
        {KINDS.map((k) => (
          <button
            key={k}
            data-testid={`admin-filter-${k}`}
            onClick={() => setFilter(k)}
            className={`font-mono-data text-[0.6rem] tracking-[0.18em] px-4 py-2 border transition-colors ${filter === k ? "bg-brass text-ink border-brass" : "border-white/20 text-white/60 hover:bg-white/10"}`}
          >
            {k.toUpperCase()}
          </button>
        ))}
      </div>

      <div className="border border-white/12 overflow-x-auto" data-testid="admin-table">
        <table className="w-full text-left text-[0.8rem]">
          <thead>
            <tr className="border-b border-white/12 font-mono-data text-[0.58rem] tracking-[0.2em] text-white/40">
              <th className="px-4 py-3 font-medium">DATE</th>
              <th className="px-4 py-3 font-medium">FORM</th>
              <th className="px-4 py-3 font-medium">NAME</th>
              <th className="px-4 py-3 font-medium">EMAIL</th>
              <th className="px-4 py-3 font-medium">DETAILS</th>
              <th className="px-4 py-3 font-medium">EMAIL STATUS</th>
            </tr>
          </thead>
          <tbody>
            {shown.map((s, i) => (
              <tr key={i} className="border-b border-white/8 hover:bg-white/5 transition-colors" data-testid={`admin-row-${i}`}>
                <td className="px-4 py-3 font-mono-data text-[0.66rem] text-white/55 whitespace-nowrap">
                  {s.created_at ? new Date(s.created_at).toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "—"}
                </td>
                <td className="px-4 py-3">
                  <span className={`font-mono-data text-[0.56rem] tracking-[0.14em] px-2 py-1 ${KIND_STYLE[s.kind] || "bg-white/10"}`}>{(s.kind || "?").toUpperCase()}</span>
                </td>
                <td className="px-4 py-3">{s.name}</td>
                <td className="px-4 py-3 text-white/70">{s.email}</td>
                <td className="px-4 py-3 text-white/55 max-w-[320px]">
                  {Object.entries(s.extra || {}).filter(([, v]) => v).map(([k, v]) => `${k}: ${v}`).join(" · ") || "—"}
                </td>
                <td className="px-4 py-3 font-mono-data text-[0.62rem] text-white/50">{s.email_status}</td>
              </tr>
            ))}
            {shown.length === 0 && (
              <tr><td colSpan={6} className="px-4 py-10 text-center font-mono-data text-[0.62rem] tracking-[0.2em] text-white/35">NO SUBMISSIONS</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </main>
  );
}
